import { useReducer } from 'react';
import {
    FILE_MIME_TYPES,
    resolveFileMimeType,
} from '@nordinsight/implant-core';

const supportedFileTypes: Record<
    'document' | 'image' | 'video' | 'other',
    string[]
> = {
    document: [FILE_MIME_TYPES.pdf, FILE_MIME_TYPES.doc, FILE_MIME_TYPES.docx],
    image: [FILE_MIME_TYPES.jpeg, FILE_MIME_TYPES.png],
    video: [FILE_MIME_TYPES.mp4],
    other: [FILE_MIME_TYPES.pptx, FILE_MIME_TYPES.xlsx],
};

export const DOCX_MIME_TYPE = FILE_MIME_TYPES.docx;
export const DOC_MIME_TYPE = FILE_MIME_TYPES.doc;

export const getFileMimeType = (file: File): string =>
    resolveFileMimeType({ filename: file.name, mimeType: file.type }) ?? '';

export type DocumentPreviewType = 'docx' | 'pdf' | 'unsupported';

export const getDocumentPreviewType = ({
    filename,
    mimeType,
}: {
    filename?: string;
    mimeType?: string;
}): DocumentPreviewType => {
    const resolvedMimeType = resolveFileMimeType({ filename, mimeType });
    if (resolvedMimeType === FILE_MIME_TYPES.docx) return 'docx';
    if (resolvedMimeType === FILE_MIME_TYPES.pdf) return 'pdf';
    return 'unsupported';
};

const supportedFileTypesArray: string[] =
    Object.values(supportedFileTypes).flat();

export const useValidateFile = (allowedTypes: {
    document?: boolean;
    image?: boolean;
    video?: boolean;
    other?: boolean;
}): [
    { fileName: string; errors: string[] }[],
    (error: { fileName: string; errors: string[] }) => void,
    () => void,
    (fileName: string) => void,
    (file: File) => boolean,
] => {
    type Action =
        | { type: 'ADD_ERROR'; error: { fileName: string; errors: string[] } }
        | { type: 'CLEAR_ERRORS' }
        | { type: 'REMOVE_ERROR'; fileName: string };

    function reducer(
        state: { fileName: string; errors: string[] }[],
        action: Action
    ): { fileName: string; errors: string[] }[] {
        switch (action.type) {
            case 'ADD_ERROR':
                return [...state, action.error];
            case 'CLEAR_ERRORS':
                return [];
            case 'REMOVE_ERROR':
                return state.filter(
                    (error) => error.fileName !== action.fileName
                );
            default:
                return state;
        }
    }

    const [errors, dispatch] = useReducer(reducer, []);

    const addError = (error: { fileName: string; errors: string[] }) =>
        dispatch({ type: 'ADD_ERROR', error });
    const clearErrors = () => dispatch({ type: 'CLEAR_ERRORS' });
    const removeError = (fileName: string) =>
        dispatch({ type: 'REMOVE_ERROR', fileName });
    return [
        errors,
        addError,
        clearErrors,
        removeError,
        (file: File) => {
            let newErrors: string[] = [];
            const mimeType = getFileMimeType(file);
            if (!supportedFileTypesArray.includes(mimeType)) {
                newErrors.push('Unsupported file type.');
            } else {
                if (
                    !allowedTypes.document &&
                    supportedFileTypes.document.includes(mimeType)
                ) {
                    newErrors.push('Document files are not allowed.');
                }
                if (
                    !allowedTypes.image &&
                    supportedFileTypes.image.includes(mimeType)
                ) {
                    newErrors.push('Image files are not allowed.');
                }
                if (!allowedTypes.video && file.type.includes('video')) {
                    newErrors.push('Video files are not allowed.');
                }
                if (
                    !allowedTypes.other &&
                    supportedFileTypes.other.includes(mimeType)
                ) {
                    newErrors.push(
                        'PowerPoint and Excel files are not allowed.'
                    );
                }
            }
            if (file.size > 500 * 1024 * 1024) {
                newErrors.push('File size is too large. Max size is 500MB.');
            }
            if (newErrors.length > 0) {
                dispatch({
                    type: 'ADD_ERROR',
                    error: { fileName: file.name, errors: newErrors },
                });
            }
            return newErrors.length === 0;
        },
    ];
};
